// src/marketing/tracking.js
// Marketing-site analytics/conversion tracking.
//
// Scope: this code only ever runs on the PUBLIC marketing site
// (empworkspace.com / www). It must NOT run on the Ops Console
// (ops.empworkspace.com) or inside a tenant portal ({slug}.empworkspace.com).
// The gating is enforced by isMarketingSite() and the caller in src/index.js.
//
// Everything is routed through Google Tag Manager — the GTM container holds the
// Google Ads conversion tag and the Meta Pixel tag, both triggered by the
// `trial_signup` dataLayer event configured in the GTM UI. No ad-network IDs are
// hardcoded here; the only build-time value is REACT_APP_GTM_ID.

import { getSubdomain } from '../config/subdomainResolver';

const GTM_ID = process.env.REACT_APP_GTM_ID;

// localStorage keys used to persist ad click identifiers from the landing URL
// through to the (possibly later) signup completion.
const CLICK_ID_STORE = 'mkt_click_ids';

// ── Gating ───────────────────────────────────────────────────────────
// True only on the bare marketing domain: not the Ops Console, and not a
// tenant subdomain. getSubdomain() returns null on empworkspace.com / www /
// localhost and for reserved subdomains (incl. `ops`).
export const isMarketingSite = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.split('.')[0];
  if (host === 'ops') return false; // belt-and-suspenders
  return getSubdomain() === null;
};

// ── Small helpers ────────────────────────────────────────────────────
const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
};

// RFC4122-ish id. Prefers crypto.randomUUID, falls back for older browsers.
export const generateEventId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (_) { /* fall through */ }
  return 'evt-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
};

// Persist gclid / fbclid (and friends) from the landing URL so attribution
// survives navigation to the signup page. Best-effort; never throws.
const captureClickIds = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = ['gclid', 'fbclid', 'wbraid', 'gbraid', 'utm_source', 'utm_medium', 'utm_campaign'];
    const found = {};
    keys.forEach((k) => { const v = params.get(k); if (v) found[k] = v; });
    if (Object.keys(found).length === 0) return;
    const existing = JSON.parse(window.localStorage.getItem(CLICK_ID_STORE) || '{}');
    window.localStorage.setItem(
      CLICK_ID_STORE,
      JSON.stringify({ ...existing, ...found, ts: Date.now() })
    );
  } catch (_) { /* private mode / disabled storage — ignore */ }
};

// Returns the attribution identifiers needed for server-side CAPI matching.
// These are NOT pushed into any client-side event — only sent to our own
// backend over the existing signup request.
export const getAttribution = () => {
  let stored = {};
  try { stored = JSON.parse(window.localStorage.getItem(CLICK_ID_STORE) || '{}'); } catch (_) { /* ignore */ }
  return {
    fbp: readCookie('_fbp'),
    fbc: readCookie('_fbc'),
    gclid: stored.gclid || null,
    fbclid: stored.fbclid || null,
    sourceUrl: typeof window !== 'undefined' ? window.location.href : null,
  };
};

// ── GTM bootstrap ────────────────────────────────────────────────────
// Injects the GTM container exactly once, only on the marketing site, only
// when REACT_APP_GTM_ID is configured.
export const initMarketingTracking = () => {
  if (!isMarketingSite()) return;
  if (!GTM_ID) return; // not configured → no-op (keeps non-prod/dev clean)
  if (window.__gtmInitialized) return;
  window.__gtmInitialized = true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`;
  document.head.appendChild(script);

  captureClickIds();
};

// ── Conversion event ─────────────────────────────────────────────────
// Pushes the single `trial_signup` event that GTM triggers fire off.
// Guaranteed to fire at most once per page session (guards double-clicks,
// React re-renders / StrictMode double-invokes, and back-button returns).
let trialSignupPushed = false;

export const trackTrialSignup = ({ eventId, planTier = 'trial' } = {}) => {
  if (trialSignupPushed) return;
  if (!isMarketingSite()) return;
  trialSignupPushed = true;

  window.dataLayer = window.dataLayer || [];
  // NOTE: strictly non-PII — no email, name, company name or slug here.
  window.dataLayer.push({
    event: 'trial_signup',
    event_id: eventId,        // shared with server-side CAPI for dedup
    plan_tier: planTier,      // always 'trial' at signup today
    signup_method: 'email',
    value: 0,                 // trial = no immediate revenue
    currency: 'INR',
  });
};
